import React, { useState, useEffect } from 'react';
import { createApiUrl } from '../../config/api';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Alert,
  Typography,
  Autocomplete,
  FormHelperText,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';

const TaskForm = ({ task, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    subject: '',
    notes: '',
    start_time: '',
    end_time: '',
    customer_id: '',
    task_status_id: '',
    task_priority_id: '',
    task_type_id: '',
    sales_person_id: '',
    assigned_user_id: '',
    rating_id: '',
    email: '',
    phone_number: '',
    mobile_phone_number: '',
    task_phone_number_type_id: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Metadata
  const [customers, setCustomers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskPriorities, setTaskPriorities] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [taskRatings, setTaskRatings] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [users, setUsers] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);

  // Initialize form data if editing
  useEffect(() => {
    if (task) {
      setFormData({
        subject: task.subject || '',
        notes: task.notes || '',
        start_time: task.start_time ? new Date(task.start_time).toISOString().slice(0, 16) : '',
        end_time: task.end_time ? new Date(task.end_time).toISOString().slice(0, 16) : '',
        customer_id: task.customer_id || '',
        task_status_id: task.task_status_id || '',
        task_priority_id: task.task_priority_id || '',
        task_type_id: task.task_type_id || '',
        sales_person_id: task.sales_person_id || '',
        assigned_user_id: task.assigned_user_id || '',
        rating_id: task.rating_id || '',
        email: task.email || '',
        phone_number: task.phone_number || '',
        mobile_phone_number: task.mobile_phone_number || '',
        task_phone_number_type_id: task.task_phone_number_type_id || ''
      });
    } else {
      // Set default dates for new tasks
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour later
      setFormData(prev => ({
        ...prev,
        start_time: now.toISOString().slice(0, 16),
        end_time: endTime.toISOString().slice(0, 16)
      }));
    }
  }, [task]);

  // Fetch metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // TKT-187: the customer dropdown was only loading the default
        // 10 customers, so common names like Doug Dolson / Naomi never
        // appeared. Ask for a much larger page so the in-memory
        // Autocomplete actually has the data to filter against.
        // is_active=false here means "no active filter applied" - the
        // server treats it as "include inactive too" so we still find
        // imported legacy records that haven't been marked active.
        const [typesRes, prioritiesRes, statusesRes, ratingsRes, customersRes, usersRes, salesPersonsRes, phoneTypesRes] = await Promise.all([
          fetch(createApiUrl('api/tasks/lookup/types'), { headers }),
          fetch(createApiUrl('api/tasks/lookup/priorities'), { headers }),
          fetch(createApiUrl('api/tasks/lookup/statuses'), { headers }),
          fetch(createApiUrl('api/tasks/lookup/ratings'), { headers }),
          fetch(createApiUrl('api/customers?limit=10000&is_active=false'), { headers }),
          fetch(createApiUrl('api/users'), { headers }).catch(() => ({ ok: false, json: () => ({ users: [] }) })),
          fetch(createApiUrl('api/customers/lookups/sales-persons/search?limit=500'), { headers }).catch(() => ({ ok: false, json: () => ({ sales_persons: [] }) })),
          fetch(createApiUrl('api/tasks/lookup/phone-types'), { headers }).catch(() => ({ ok: false, json: () => ({ data: [] }) }))
        ]);

        const [typesData, prioritiesData, statusesData, ratingsData, customersData, usersData, salesPersonsData, phoneTypesData] = await Promise.all([
          typesRes.json(),
          prioritiesRes.json(),
          statusesRes.json(),
          ratingsRes.json(),
          customersRes.json(),
          usersRes.json(),
          salesPersonsRes.json(),
          phoneTypesRes.json()
        ]);

        // Debug logging for development
        if (process.env.NODE_ENV === 'development') {
          console.log('Task Form API Responses:', {
            typesRes: typesRes.ok,
            prioritiesRes: prioritiesRes.ok,
            statusesRes: statusesRes.ok,
            ratingsRes: ratingsRes.ok,
            customersRes: customersRes.ok,
            usersRes: usersRes.ok,
            salesPersonsRes: salesPersonsRes.ok,
            phoneTypesRes: phoneTypesRes.ok
          });
        }

        if (typesRes.ok) setTaskTypes(typesData.data || []);
        if (prioritiesRes.ok) setTaskPriorities(prioritiesData.data || []);
        if (statusesRes.ok) setTaskStatuses(statusesData.data || []);
        if (ratingsRes.ok) setTaskRatings(ratingsData.data || []);
        if (customersRes.ok) setCustomers(customersData.customers || []);
        if (usersRes.ok) setUsers(usersData.users || []);
        if (salesPersonsRes.ok) setSalesPersons(salesPersonsData.sales_persons || []);
        if (phoneTypesRes.ok) setPhoneTypes(phoneTypesData.data || []);
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetadata();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-populate contact info when customer is selected
    if (field === 'customer_id' && value) {
      const selectedCustomer = customers.find(c => c.id === value);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          email: selectedCustomer.email_address || '',
          phone_number: selectedCustomer.phone_number || '',
          mobile_phone_number: selectedCustomer.mobile_number || ''
        }));
      }
    }

    // Auto-set end time when start time changes (1 hour later)
    if (field === 'start_time' && value) {
      const startTime = new Date(value);
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);
      setFormData(prev => ({
        ...prev,
        end_time: endTime.toISOString().slice(0, 16)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.customer_id) {
      newErrors.customer_id = 'Customer is required';
    }
    if (!formData.task_status_id) {
      newErrors.task_status_id = 'Status is required';
    }
    if (!formData.task_priority_id) {
      newErrors.task_priority_id = 'Priority is required';
    }
    if (!formData.task_type_id) {
      newErrors.task_type_id = 'Task type is required';
    }
    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }
    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }
    if (formData.start_time && formData.end_time && new Date(formData.start_time) >= new Date(formData.end_time)) {
      newErrors.end_time = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitLoading(true);
      
      const url = task ? createApiUrl(`api/tasks/${task.id}`) : createApiUrl('api/tasks');
      const method = task ? 'PUT' : 'POST';
      
      // CR-007: the API needs the login token; without it every save got 401.
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...formData,
          // Convert datetime-local format to ISO string
          start_time: new Date(formData.start_time).toISOString(),
          end_time: new Date(formData.end_time).toISOString()
        }),
      });

      const data = await response.json();

      if (response.ok) {
        onSubmit(data.data);
      } else {
        setErrors({ submit: data.message || 'Failed to save task' });
      }
    } catch (error) {
      setErrors({ submit: 'Network error occurred' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const getCustomerDisplayName = (customer) => {
    const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    return customer.company_name 
      ? `${name} (${customer.company_name})`
      : name || customer.email_address || `Customer #${customer.id}`;
  };

  const getUserDisplayName = (user) => {
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return `${name} (${user.username})`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TaskIcon />
        {task ? 'Edit Task' : 'Create New Task'}
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Basic Task Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Task Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Task Subject *"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                error={!!errors.subject}
                helperText={errors.subject}
                placeholder="Enter a clear, descriptive subject for this task"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.task_type_id}>
                <InputLabel>Task Type *</InputLabel>
                <Select
                  value={formData.task_type_id}
                  label="Task Type *"
                  onChange={(e) => handleInputChange('task_type_id', e.target.value)}
                >
                  {taskTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.task_type_id && <FormHelperText>{errors.task_type_id}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.task_priority_id}>
                <InputLabel>Priority *</InputLabel>
                <Select
                  value={formData.task_priority_id}
                  label="Priority *"
                  onChange={(e) => handleInputChange('task_priority_id', e.target.value)}
                >
                  {taskPriorities.map((priority) => (
                    <MenuItem key={priority.id} value={priority.id}>
                      {priority.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.task_priority_id && <FormHelperText>{errors.task_priority_id}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.task_status_id}>
                <InputLabel>Status *</InputLabel>
                <Select
                  value={formData.task_status_id}
                  label="Status *"
                  onChange={(e) => handleInputChange('task_status_id', e.target.value)}
                >
                  {taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.task_status_id && <FormHelperText>{errors.task_status_id}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Rating</InputLabel>
                <Select
                  value={formData.rating_id}
                  label="Rating"
                  onChange={(e) => handleInputChange('rating_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {taskRatings.map((rating) => (
                    <MenuItem key={rating.id} value={rating.id}>
                      {rating.rating_name} ({rating.rating_number}/5)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes/Description"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add any additional details, instructions, or notes for this task"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Customer Selection */}
          <Typography variant="h6" gutterBottom>
            Customer Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={customers.find(c => c.id === formData.customer_id) || null}
                onChange={(event, newValue) => {
                  handleInputChange('customer_id', newValue ? newValue.id : '');
                }}
                options={customers}
                getOptionLabel={getCustomerDisplayName}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options.slice(0, 50);
                  
                  const filtered = options.filter(option => {
                    const searchText = inputValue.toLowerCase();
                    const name = `${option.first_name || ''} ${option.last_name || ''}`.toLowerCase();
                    const company = (option.company_name || '').toLowerCase();
                    const email = (option.email_address || '').toLowerCase();
                    
                    return name.includes(searchText) || 
                           company.includes(searchText) || 
                           email.includes(searchText);
                  });
                  
                  return filtered.slice(0, 100);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer *"
                    error={!!errors.customer_id}
                    helperText={errors.customer_id || "Search by name, company, or email"}
                    fullWidth
                    placeholder="Start typing to search for a customer..."
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ padding: '12px 16px' }}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" fontWeight="medium">
                        {getCustomerDisplayName(option)}
                      </Typography>
                      {option.email_address && (
                        <Typography variant="caption" color="text.secondary">
                          📧 {option.email_address}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                noOptionsText="No customers found"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Scheduling */}
          <Typography variant="h6" gutterBottom>
            Schedule
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Time *"
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => handleInputChange('start_time', e.target.value)}
                error={!!errors.start_time}
                helperText={errors.start_time}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Time *"
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => handleInputChange('end_time', e.target.value)}
                error={!!errors.end_time}
                helperText={errors.end_time}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Assignment */}
          <Typography variant="h6" gutterBottom>
            Assignment
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                value={salesPersons.find(sp => sp.id === formData.sales_person_id) || null}
                onChange={(event, newValue) => {
                  handleInputChange('sales_person_id', newValue ? newValue.id : '');
                }}
                options={salesPersons}
                getOptionLabel={(sp) => sp.display_name || `${sp.first_name} ${sp.last_name}`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sales Person"
                    placeholder="Search for sales person..."
                  />
                )}
                noOptionsText="No sales persons found"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              {/* TKT-187: merge sales persons into the Assigned To list
                  so the office can route a task to a sales rep without
                  scrolling through two parallel pickers. Sales persons
                  that already exist as users are de-duplicated by id. */}
              {(() => {
                const userIds = new Set((users || []).map((u) => u.id));
                const salesAsUsers = (salesPersons || [])
                  .filter((sp) => sp && sp.user_id && !userIds.has(sp.user_id))
                  .map((sp) => ({
                    id: sp.user_id,
                    first_name: sp.first_name || (sp.display_name || '').split(' ')[0] || '',
                    last_name: sp.last_name || (sp.display_name || '').split(' ').slice(1).join(' ') || '',
                    email: sp.email || '',
                    _source: 'sales',
                  }));
                const assigneeOptions = [...(users || []), ...salesAsUsers];
                return (
                  <Autocomplete
                    value={assigneeOptions.find(u => u.id === formData.assigned_user_id) || null}
                    onChange={(event, newValue) => {
                      handleInputChange('assigned_user_id', newValue ? newValue.id : '');
                    }}
                    options={assigneeOptions}
                    getOptionLabel={(opt) =>
                      `${getUserDisplayName(opt)}${opt._source === 'sales' ? ' (Sales)' : ''}`
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned To"
                        placeholder="Search for user or sales rep..."
                      />
                    )}
                    noOptionsText="No users found"
                  />
                );
              })()}
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Contact Information */}
          <Typography variant="h6" gutterBottom>
            Contact Information (Optional)
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Override customer email if needed"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Phone Type</InputLabel>
                <Select
                  value={formData.task_phone_number_type_id}
                  label="Phone Type"
                  onChange={(e) => handleInputChange('task_phone_number_type_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {phoneTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                placeholder="Override customer phone if needed"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile_phone_number}
                onChange={(e) => handleInputChange('mobile_phone_number', e.target.value)}
                placeholder="Override customer mobile if needed"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={submitLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          loading={submitLoading}
          disabled={submitLoading}
          sx={{ bgcolor: '#1976d2' }}
        >
          {submitLoading ? 'Saving...' : (task ? 'Update Task' : 'Create Task')}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default TaskForm; 